import { Circle, Group } from "react-konva";

interface DotGridLayerProps {
  /** Grid spacing in board coordinates. */
  size: number;
  /** Current viewport for visibility culling (board → screen). */
  viewport: { scale: number; x: number; y: number };
  /** Stage size in screen pixels — defines the visible window we tile over. */
  stageW: number;
  stageH: number;
  /** Dot color (theme-dependent). */
  color: string;
  /** Dot radius in board coordinates. */
  radius?: number;
}

/**
 * Renders a sparse dot grid as the Whiteboard background. We compute the
 * visible board-space window from the current viewport and only draw the
 * dots inside it — keeping the node count bounded regardless of zoom level.
 */
export function DotGridLayer({
  size,
  viewport,
  stageW,
  stageH,
  color,
  radius = 1.5,
}: DotGridLayerProps) {
  if (size <= 0 || stageW <= 0 || stageH <= 0) return null;

  // Visible board-space window. Anything outside [boardX0..boardX1] × […]
  // is off-screen at the current scale/pan.
  const boardX0 = -viewport.x / viewport.scale;
  const boardY0 = -viewport.y / viewport.scale;
  const boardX1 = boardX0 + stageW / viewport.scale;
  const boardY1 = boardY0 + stageH / viewport.scale;

  // Hard cap to avoid pathological node counts when zoomed way out.
  const cols = Math.floor((boardX1 - boardX0) / size) + 2;
  const rows = Math.floor((boardY1 - boardY0) / size) + 2;
  const maxDots = 6000;
  if (cols * rows > maxDots) {
    // Skip rendering when the user is zoomed so far out the grid becomes a
    // dense mush; reappears once the user zooms back in.
    return null;
  }

  const startX = Math.floor(boardX0 / size) * size;
  const startY = Math.floor(boardY0 / size) * size;

  const dots: React.ReactElement[] = [];
  for (let y = startY; y <= boardY1; y += size) {
    for (let x = startX; x <= boardX1; x += size) {
      dots.push(
        <Circle
          key={`${x}:${y}`}
          x={x}
          y={y}
          radius={radius}
          fill={color}
          listening={false}
          perfectDrawEnabled={false}
        />,
      );
    }
  }
  return <Group listening={false}>{dots}</Group>;
}
